import type { ColumnsType, ColumnType } from 'antd/es/table';
import * as XLSX from 'xlsx';

/**
 * 그룹 헤더 정보를 포함한 컬럼 구조
 */
interface ColumnInfo {
  groupTitle: string | null; // 그룹 헤더 타이틀
  columnTitle: string; // 실제 컬럼 타이틀
  dataKey: string; // 데이터 접근 키
}

/**
 * 그룹 헤더 정보와 render 함수를 포함한 컬럼 구조
 */
interface ColumnInfoWithRender extends ColumnInfo {
  render?: (value: any, row: any, index: number) => any;
}

/**
 * CrossTableChart의 columns와 dataSource를 엑셀로 export하는 함수
 * 그룹 헤더가 있는 경우 2행 헤더로 표현하고 셀을 병합합니다.
 *
 * @param columns - Ant Design Table의 columns 배열
 * @param dataSource - Ant Design Table의 dataSource 배열
 * @param filename - 저장할 파일 이름 (기본값: 'cross_table_export.xlsx')
 */
export const exportMultiSeriesTableToExcel = (
  columns: ColumnsType<any>,
  dataSource: any[],
  filename: string = 'cross_table_export.xlsx',
) => {
  // 1. 그룹 헤더와 render 함수를 포함한 컬럼 정보 추출
  const extractColumnInfo = (
    cols: ColumnsType<any>,
    parentTitle: string | null = null,
  ): ColumnInfoWithRender[] => {
    const columnInfos: ColumnInfoWithRender[] = [];

    cols.forEach((col) => {
      if ('children' in col && col.children) {
        // 중첩된 컬럼이 있는 경우 (그룹 헤더)
        const groupTitle = extractTitle(col.title);
        columnInfos.push(...extractColumnInfo(col.children, groupTitle));
      } else {
        // 일반 컬럼
        const columnTitle = extractTitle(col.title);
        const dataKey = extractDataKey(col);

        if (dataKey) {
          columnInfos.push({
            groupTitle: parentTitle,
            columnTitle,
            dataKey,
            render: 'render' in col ? col.render : undefined,
          });
        }
      }
    });

    return columnInfos;
  };

  // 2. 컬럼 정보 추출
  const columnInfos = extractColumnInfo(columns);

  // 3. 그룹 헤더가 있는지 확인
  const hasGroupHeaders = columnInfos.some((info) => info.groupTitle !== null);

  // 4. 워크시트 데이터 생성
  const worksheetData: any[][] = [];

  if (hasGroupHeaders) {
    // 2행 헤더 구조: 첫 행은 그룹 헤더, 둘째 행은 컬럼 헤더
    const groupHeaderRow: string[] = [];
    const columnHeaderRow: string[] = [];

    columnInfos.forEach((info) => {
      groupHeaderRow.push(info.groupTitle || '');
      columnHeaderRow.push(info.columnTitle);
    });

    worksheetData.push(groupHeaderRow);
    worksheetData.push(columnHeaderRow);
  } else {
    // 단일 행 헤더
    const headerRow = columnInfos.map((info) => info.columnTitle);
    worksheetData.push(headerRow);
  }

  // 5. 데이터 행 추가
  dataSource.forEach((row, rowIndex) => {
    const dataRow: any[] = [];

    columnInfos.forEach((info) => {
      let value = row[info.dataKey];

      // render 함수가 있으면 사용하여 값을 변환
      if (info.render) {
        try {
          const rendered = info.render(value, row, rowIndex);

          // render 함수의 반환값 처리
          if (rendered && typeof rendered === 'object') {
            // TableCellRenderer를 반환하는 경우: { props: {...}, children: <TableCellRenderer /> }
            if (rendered.children && rendered.children.props) {
              // TableCellRenderer의 value prop에서 실제 값 추출
              const cellValue = rendered.children.props.value;
              const isPercentage = rendered.children.props.isPercentage;

              if (cellValue === null || cellValue === undefined) {
                value = '-';
              } else if (isPercentage) {
                value = `${cellValue}%`;
              } else {
                value = cellValue;
              }
            } else if (rendered.props) {
              // React Element인 경우 텍스트 추출
              value = extractTextFromReactElement(rendered);
            } else {
              value = rendered;
            }
          } else {
            value = rendered;
          }
        } catch (error) {
          // render 함수 실행 중 에러가 발생하면 원본 값 사용
          console.warn('Error rendering cell value:', error);
        }
      }

      // 값 처리
      if (value !== null && value !== undefined) {
        if (typeof value === 'object' && value.props) {
          // React element인 경우 텍스트 추출
          value = extractTextFromReactElement(value);
        } else if (typeof value === 'object') {
          // 일반 객체인 경우 JSON 문자열로 변환
          value = JSON.stringify(value);
        }
      }

      dataRow.push(value ?? '');
    });

    worksheetData.push(dataRow);
  });

  // 6. 워크시트 생성
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // 7. 그룹 헤더가 있는 경우 셀 병합 처리
  if (hasGroupHeaders) {
    const merges: XLSX.Range[] = [];

    // 그룹별로 컬럼 인덱스 매핑
    const groupMap = new Map<string, number[]>();

    columnInfos.forEach((info, index) => {
      const groupKey = info.groupTitle || `__no_group_${index}__`;

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, []);
      }
      groupMap.get(groupKey)!.push(index);
    });

    // 각 그룹에 대해 병합 처리
    groupMap.forEach((colIndices, groupKey) => {
      if (groupKey.startsWith('__no_group_')) {
        // 그룹이 없는 컬럼은 세로로 병합 (첫 행과 둘째 행)
        const colIndex = colIndices[0];
        merges.push({
          s: { r: 0, c: colIndex },
          e: { r: 1, c: colIndex },
        });
      } else if (colIndices.length > 1) {
        // 그룹에 속한 컬럼이 여러 개인 경우 가로로 병합 (첫 행)
        merges.push({
          s: { r: 0, c: colIndices[0] },
          e: { r: 0, c: colIndices[colIndices.length - 1] },
        });
      }
      // 그룹에 속한 컬럼이 1개인 경우 세로로 병합
      else if (colIndices.length === 1) {
        merges.push({
          s: { r: 0, c: colIndices[0] },
          e: { r: 1, c: colIndices[0] },
        });
      }
    });

    worksheet['!merges'] = merges;
  }

  // 8. 컬럼 너비 자동 조정
  const columnWidths = columnInfos.map((info) => {
    const groupLength = info.groupTitle ? info.groupTitle.length : 0;
    const columnLength = info.columnTitle.length;
    return {
      wch: Math.max(groupLength, columnLength, 10),
    };
  });
  worksheet['!cols'] = columnWidths;

  // 9. 워크북 생성 및 다운로드
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Cross Table');

  XLSX.writeFile(workbook, filename);
};

/**
 * 컬럼 타이틀 추출 (React Element 포함)
 */
const extractTitle = (title: any): string => {
  if (!title) return '';
  if (typeof title === 'string') return title;
  if (typeof title === 'number') return String(title);
  return extractTextFromReactElement(title);
};

/**
 * 데이터 키 추출
 */
const extractDataKey = (col: ColumnType<any>): string => {
  const key = 'dataIndex' in col ? col.dataIndex : col.key;
  if (!key) return '';
  return Array.isArray(key) ? key.join('.') : String(key);
};

/**
 * React Element에서 텍스트 추출 (재귀적으로 처리)
 */
const extractTextFromReactElement = (element: any): string => {
  if (!element) return '';

  if (typeof element === 'string' || typeof element === 'number') {
    return String(element);
  }

  if (element.props) {
    if (element.props.children) {
      const children = element.props.children;

      if (Array.isArray(children)) {
        return children.map(extractTextFromReactElement).join(' ');
      }

      return extractTextFromReactElement(children);
    }
  }

  return '';
};
